import React, { useState, useEffect, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Label,
  FormGroup,
  Button,
  Input,
  Alert,
  Spinner,
  Table,
  Badge
} from 'reactstrap';
import { connect } from 'react-redux';
import Select from 'react-select';
import SweetAlert from 'react-bootstrap-sweetalert';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { getBatchList, getBatchChildrenList } from '../../api/batch';
import { getOfferingList } from '../../api/offering';
import {
  applyBatchPromotionalDiscount,
  fetchAllTransactionsList,
  bulkCreateQuarterlyTransactions,
  bulkUpdateInstallmentSettings
} from '../../api/transaction';

const ADDRESS_OPTIONS = ['Kandivali', 'Malad', 'Borivali', 'Other'];

const PromotionalDiscount = ({ authUser }) => {
  const [breadcrumbItems] = useState([
    { title: 'Batch', link: '/batches' },
    { title: 'Bulk Update', link: '#' }
  ]);

  const [selectionMode, setSelectionMode] = useState('batch');
  const [batchOptions, setBatchOptions] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(true);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [offeringOptions, setOfferingOptions] = useState([]);
  const [offeringsLoading, setOfferingsLoading] = useState(true);
  const [selectedOffering, setSelectedOffering] = useState(null);

  const [activeAction, setActiveAction] = useState('discount');

  const [discountAmount, setDiscountAmount] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState([]);

  const [batchAddresses, setBatchAddresses] = useState({});
  const [showCreateConfirm, setShowCreateConfirm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createResults, setCreateResults] = useState([]);

  const [settingsDueDay, setSettingsDueDay] = useState('');
  const [settingsAlertDay, setSettingsAlertDay] = useState('');
  const [settingsPenaltyDay, setSettingsPenaltyDay] = useState('');
  const [settingsPenaltyMode, setSettingsPenaltyMode] = useState('');
  const [settingsPenaltyAmount, setSettingsPenaltyAmount] = useState('');
  const [settingsQuarters, setSettingsQuarters] = useState([1, 2, 3, 4]);
  const [showSettingsConfirm, setShowSettingsConfirm] = useState(false);
  const [updatingSettings, setUpdatingSettings] = useState(false);
  const [settingsResults, setSettingsResults] = useState([]);

  const [globalError, setGlobalError] = useState('');

  const [previewRows, setPreviewRows] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const prevSelectedRef = useRef([]);

  useEffect(() => {
    async function loadData() {
      setBatchesLoading(true);
      setOfferingsLoading(true);
      const [batchData, offeringData] = await Promise.all([
        getBatchList(1, 500),
        getOfferingList(1, 500)
      ]);
      const batches = batchData?.data || [];
      setBatchOptions(
        batches.map((b) => ({
          value: b._id,
          label: b.name,
          childCount: b.children?.length ?? 0,
          totalFee: b.totalFee ?? null,
          dueDay: b.due_day ?? 10,
          penaltyMode: b.penaltyMode || 'percentage',
          offeringId: b.offering?._id || b.offering || null,
          quarter1: b.quarter1,
          quarter2: b.quarter2,
          quarter3: b.quarter3,
          quarter4: b.quarter4,
          quarters: b.quarters
        }))
      );
      setBatchesLoading(false);
      const offerings = offeringData?.data || [];
      setOfferingOptions(
        offerings.map((o) => ({ value: o._id, label: o.name }))
      );
      setOfferingsLoading(false);
    }
    loadData();
  }, []);

  useEffect(() => {
    const prevIds = prevSelectedRef.current.map((b) => b.value).sort().join(',');
    const nextIds = selectedBatches.map((b) => b.value).sort().join(',');
    prevSelectedRef.current = selectedBatches;
    if (prevIds === nextIds) return;

    setResults([]);
    setCreateResults([]);
    setSettingsResults([]);
    setGlobalError('');

    const currentIds = new Set(selectedBatches.map((b) => b.value));
    setBatchAddresses((prev) => {
      const next = {};
      for (const id of currentIds) {
        if (prev[id]) next[id] = prev[id];
      }
      return next;
    });

    if (selectedBatches.length === 0) {
      setPreviewRows([]);
      return;
    }

    async function loadPreview() {
      setPreviewLoading(true);
      const rows = [];

      for (const batch of selectedBatches) {
        const [childData, txData] = await Promise.all([
          getBatchChildrenList(batch.value, 1, 500),
          fetchAllTransactionsList({ batchId: batch.value, limit: 500 })
        ]);

        const children = childData?.data || [];
        const transactions = txData?.response?.data || [];

        const txByChild = {};
        for (const tx of transactions) {
          const key = tx.childrenId?._id || tx.childrenId;
          if (!txByChild[key]) txByChild[key] = [];
          txByChild[key].push(tx);
        }

        const firstChildId = children[0]?._id;
        const firstTx = firstChildId ? (txByChild[firstChildId] || [])[0] : null;
        const discountPerInstallment = firstTx?.discountAmount || 0;
        const batchPromoPercent =
          discountPerInstallment > 0 && batch.totalFee
            ? Number(((discountPerInstallment * 4) / batch.totalFee * 100).toFixed(2))
            : null;
        const batchTotalDiscount =
          discountPerInstallment > 0 ? Number((discountPerInstallment * 4).toFixed(2)) : null;

        for (let ci = 0; ci < children.length; ci++) {
          const child = children[ci];
          const childId = child._id;
          const childTxns = txByChild[childId] || [];

          for (let q = 1; q <= 4; q++) {
            const tx = childTxns.find((t) => t.quarter === q);
            const isBatchHeader = ci === 0 && q === 1;
            rows.push({
              batchId: batch.value,
              batchName: batch.label,
              batchTotalFee: batch.totalFee,
              batchPromoPercent: isBatchHeader ? batchPromoPercent : undefined,
              batchTotalDiscount: isBatchHeader ? batchTotalDiscount : undefined,
              childId,
              childName: child.name,
              quarter: q,
              hasTx: !!tx,
              amountPerTx: tx ? tx.totalAmountToPay : null,
              existingDiscount: tx ? tx.discountAmount || 0 : null,
              showBatch: isBatchHeader,
              batchSpan: children.length * 4,
              showChild: q === 1,
              childSpan: 4
            });
          }
        }
      }

      setPreviewRows(rows);
      setPreviewLoading(false);
    }

    loadPreview();
  }, [selectedBatches]);

  const discountAmountNum = parseFloat(discountAmount);
  const isDiscountValid =
    selectedBatches.length > 0 && !isNaN(discountAmountNum) && discountAmountNum > 0;
  const isCreateValid =
    selectedBatches.length > 0 && selectedBatches.every((b) => batchAddresses[b.value]);
  const settingsDueDayNum = settingsDueDay !== '' ? parseInt(settingsDueDay, 10) : null;
  const settingsAlertDayNum = settingsAlertDay !== '' ? parseInt(settingsAlertDay, 10) : null;
  const settingsPenaltyDayNum = settingsPenaltyDay !== '' ? parseInt(settingsPenaltyDay, 10) : null;
  const atLeastOneField =
    (settingsDueDayNum !== null && !isNaN(settingsDueDayNum)) ||
    (settingsAlertDayNum !== null && !isNaN(settingsAlertDayNum)) ||
    (settingsPenaltyDayNum !== null && !isNaN(settingsPenaltyDayNum)) ||
    settingsPenaltyMode !== '' ||
    settingsPenaltyAmount !== '';
  const isSettingsValid =
    selectedBatches.length > 0 &&
    settingsQuarters.length > 0 &&
    atLeastOneField &&
    (settingsDueDayNum === null || (!isNaN(settingsDueDayNum) && settingsDueDayNum >= 1 && settingsDueDayNum <= 28)) &&
    (settingsAlertDayNum === null || (!isNaN(settingsAlertDayNum) && settingsAlertDayNum >= 0)) &&
    (settingsPenaltyDayNum === null || (!isNaN(settingsPenaltyDayNum) && settingsPenaltyDayNum >= 0));

  async function handleApply() {
    setShowConfirm(false);
    setApplying(true);
    setResults([]);
    setGlobalError('');

    const batchResults = [];
    for (const batch of selectedBatches) {
      const { response, error } = await applyBatchPromotionalDiscount(batch.value, discountAmountNum);
      batchResults.push({
        batchId: batch.value,
        name: batch.label,
        success: !!response,
        message: response
          ? `${response.data?.childrenCount ?? '?'} children updated — ₹${response.data?.discountPerInstallment ?? '?'}/installment`
          : error
      });
    }
    setResults(batchResults);
    setApplying(false);
  }

  async function handleUpdateSettings() {
    setShowSettingsConfirm(false);
    setUpdatingSettings(true);
    setSettingsResults([]);
    setGlobalError('');

    const batchIds = selectedBatches.map((b) => b.value);
    const { response, error } = await bulkUpdateInstallmentSettings({
      batchIds,
      selectedQuarters: settingsQuarters,
      ...(settingsDueDayNum !== null ? { dueDay: settingsDueDayNum } : {}),
      ...(settingsAlertDayNum !== null ? { alertDay: settingsAlertDayNum } : {}),
      ...(settingsPenaltyDayNum !== null ? { penaltyDay: settingsPenaltyDayNum } : {}),
      ...(settingsPenaltyMode !== '' ? { penaltyMode: settingsPenaltyMode } : {}),
      ...(settingsPenaltyAmount !== '' ? { penaltyAmount: Number(settingsPenaltyAmount) } : {})
    });
    if (response) {
      setSettingsResults(response.data?.results || []);
    } else {
      setGlobalError(error || 'Failed to update settings');
    }
    setUpdatingSettings(false);
  }

  async function handleCreate() {
    setShowCreateConfirm(false);
    setCreating(true);
    setCreateResults([]);
    setGlobalError('');

    const batches = selectedBatches.map((b) => ({
      batchId: b.value,
      address: batchAddresses[b.value]
    }));
    const { response, error } = await bulkCreateQuarterlyTransactions(batches);
    if (response) {
      setCreateResults(response.data?.results || []);
    } else {
      setGlobalError(error || 'Failed to create transactions');
    }
    setCreating(false);
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Bulk Update" breadcrumbItems={breadcrumbItems} />

          <Row>
            <Col lg={5}>
              <Card>
                <CardBody>
                  <FormGroup>
                    <Label className="d-block mb-1">Select By</Label>
                    <div className="d-flex mb-2" style={{ gap: 20 }}>
                      {[{ value: 'batch', label: 'Individual Batches' }, { value: 'offering', label: 'Offering' }].map((opt) => (
                        <div key={opt.value} className="form-check">
                          <input
                            type="radio"
                            id={`selMode-${opt.value}`}
                            name="selectionMode"
                            className="form-check-input"
                            checked={selectionMode === opt.value}
                            onChange={() => {
                              setSelectionMode(opt.value);
                              setSelectedBatches([]);
                              setSelectedOffering(null);
                            }}
                          />
                          <label htmlFor={`selMode-${opt.value}`} className="form-check-label" style={{ cursor: 'pointer', fontWeight: selectionMode === opt.value ? 600 : 400 }}>
                            {opt.label}
                          </label>
                        </div>
                      ))}
                    </div>

                    {selectionMode === 'offering' ? (
                      <>
                        <Select
                          isLoading={offeringsLoading}
                          options={offeringOptions}
                          value={selectedOffering}
                          onChange={(val) => {
                            setSelectedOffering(val);
                            if (val) {
                              const matched = batchOptions.filter((b) => b.offeringId === val.value);
                              setSelectedBatches(matched);
                            } else {
                              setSelectedBatches([]);
                            }
                          }}
                          placeholder="Select an offering..."
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                        {selectedOffering && (
                          <small className="text-muted d-block mt-1">
                            {selectedBatches.length} batch{selectedBatches.length !== 1 ? 'es' : ''} selected
                          </small>
                        )}
                      </>
                    ) : (
                      <Select
                        isMulti
                        isLoading={batchesLoading}
                        options={batchOptions}
                        value={selectedBatches}
                        onChange={(val) => setSelectedBatches(val || [])}
                        placeholder="Search and select batches..."
                        noOptionsMessage={() => 'No batches found'}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        formatOptionLabel={(opt) => (
                          <span>
                            {opt.label}{' '}
                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                              ({opt.childCount} children)
                            </span>
                          </span>
                        )}
                      />
                    )}
                  </FormGroup>

                  {selectedBatches.length > 0 && (
                    <FormGroup className="mb-4">
                      <Label className="d-block mb-2">Action</Label>
                      <div>
                        {[
                          { value: 'discount', label: 'Apply Promotional Discount' },
                          { value: 'create', label: 'Create Transactions' },
                          { value: 'settings', label: 'Update Installment Settings' }
                        ].map((opt) => (
                          <div key={opt.value} className="form-check mb-1">
                            <input
                              type="radio"
                              id={`action-${opt.value}`}
                              name="activeAction"
                              value={opt.value}
                              checked={activeAction === opt.value}
                              onChange={() => setActiveAction(opt.value)}
                              className="form-check-input"
                            />
                            <label
                              htmlFor={`action-${opt.value}`}
                              className="form-check-label"
                              style={{ cursor: 'pointer', fontWeight: activeAction === opt.value ? 600 : 400 }}
                            >
                              {opt.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </FormGroup>
                  )}

                  {selectedBatches.length > 0 && activeAction === 'discount' && (
                    <>
                      <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                        The discount will be applied equally across all 4 installments for every
                        child currently enrolled in each selected batch. Children added after this
                        action will <strong>not</strong> receive the discount.
                      </p>
                      <FormGroup>
                        <Label>Discount Amount (₹)</Label>
                        <Input
                          type="number"
                          min={1}
                          value={discountAmount}
                          onChange={(e) => setDiscountAmount(e.target.value)}
                          placeholder="e.g. 500"
                          style={{ maxWidth: 160 }}
                        />
                        {discountAmount && !isNaN(discountAmountNum) && discountAmountNum > 0 && (
                          <small className="text-muted mt-1 d-block">
                            Per installment = ₹{(discountAmountNum / 4).toFixed(2)}
                          </small>
                        )}
                      </FormGroup>

                      {globalError && <Alert color="danger">{globalError}</Alert>}

                      <Button
                        color="primary"
                        disabled={!isDiscountValid || applying}
                        onClick={() => setShowConfirm(true)}
                      >
                        {applying ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Applying...
                          </>
                        ) : (
                          'Apply Discount'
                        )}
                      </Button>
                    </>
                  )}

                  {activeAction === 'settings' && (
                    <>
                      <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                        Updates installment settings for selected batches. Due Day and Penalty Mode
                        apply to the whole batch; other settings apply only to the selected quarters.
                      </p>
                      <FormGroup row className="align-items-center mb-2">
                        <Label sm={5}>Apply to Quarters</Label>
                        <Col sm={7}>
                          <div className="d-flex" style={{ gap: 16 }}>
                            {[1, 2, 3, 4].map((n) => (
                              <div key={n} className="form-check">
                                <input
                                  type="checkbox"
                                  id={`settingsQ${n}`}
                                  className="form-check-input"
                                  checked={settingsQuarters.includes(n)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSettingsQuarters((prev) => [...prev, n].sort());
                                    } else {
                                      setSettingsQuarters((prev) => prev.filter((q) => q !== n));
                                    }
                                  }}
                                />
                                <label htmlFor={`settingsQ${n}`} className="form-check-label" style={{ cursor: 'pointer' }}>
                                  Q{n}
                                </label>
                              </div>
                            ))}
                          </div>
                          {settingsQuarters.length === 0 && (
                            <small className="text-danger d-block mt-1">Select at least one quarter</small>
                          )}
                        </Col>
                      </FormGroup>
                      <FormGroup row className="align-items-center mb-2">
                        <Label sm={5}>Due Day of the Month (1–28)</Label>
                        <Col sm={7}>
                          <Input
                            type="number"
                            min={1}
                            max={28}
                            value={settingsDueDay}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === '') { setSettingsDueDay(''); return; }
                              const val = Math.min(28, Math.max(1, Number(raw)));
                              setSettingsDueDay(isNaN(val) ? '' : String(val));
                            }}
                            placeholder="Leave blank to keep current"
                            style={{ maxWidth: 180 }}
                          />
                        </Col>
                      </FormGroup>
                      <FormGroup row className="align-items-center mb-2">
                        <Label sm={5}>Alert Day (days before due)</Label>
                        <Col sm={7}>
                          <Input
                            type="number"
                            min={0}
                            value={settingsAlertDay}
                            onChange={(e) => setSettingsAlertDay(e.target.value)}
                            placeholder="Leave blank to keep current"
                            style={{ maxWidth: 180 }}
                          />
                        </Col>
                      </FormGroup>
                      <FormGroup row className="align-items-center mb-2">
                        <Label sm={5}>Penalty Day (grace days after due)</Label>
                        <Col sm={7}>
                          <Input
                            type="number"
                            min={0}
                            value={settingsPenaltyDay}
                            onChange={(e) => setSettingsPenaltyDay(e.target.value)}
                            placeholder="Leave blank to keep current"
                            style={{ maxWidth: 180 }}
                          />
                        </Col>
                      </FormGroup>
                      <FormGroup row className="align-items-center mb-2">
                        <Label sm={5}>Penalty Mode</Label>
                        <Col sm={7}>
                          <div>
                            {[
                              { value: '', label: 'No change' },
                              { value: 'percentage', label: 'Percentage' },
                              { value: 'daily_flat', label: 'Per Day (Flat)' }
                            ].map((opt) => (
                              <div key={opt.value || 'nochange'} className="form-check mb-1">
                                <input
                                  type="radio"
                                  id={`penaltyMode-${opt.value || 'nochange'}`}
                                  name="settingsPenaltyMode"
                                  value={opt.value}
                                  checked={settingsPenaltyMode === opt.value}
                                  onChange={() => setSettingsPenaltyMode(opt.value)}
                                  className="form-check-input"
                                />
                                <label
                                  htmlFor={`penaltyMode-${opt.value || 'nochange'}`}
                                  className="form-check-label"
                                  style={{ cursor: 'pointer', fontWeight: settingsPenaltyMode === opt.value ? 600 : 400 }}
                                >
                                  {opt.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </Col>
                      </FormGroup>
                      <FormGroup row className="align-items-center mb-3">
                        <Label sm={5}>
                          {settingsPenaltyMode === 'daily_flat' ? 'Penalty Amount (₹/day)' : 'Penalty (%)'}
                        </Label>
                        <Col sm={7}>
                          <Input
                            type="number"
                            min={0}
                            value={settingsPenaltyAmount}
                            onChange={(e) => setSettingsPenaltyAmount(e.target.value)}
                            placeholder="Leave blank to keep current"
                            style={{ maxWidth: 180 }}
                          />
                          {settingsPenaltyAmount !== '' && (
                            <small className="text-muted d-block mt-1">
                              {settingsPenaltyMode === 'daily_flat'
                                ? `₹${settingsPenaltyAmount}/day after grace period`
                                : settingsPenaltyMode === 'percentage'
                                  ? `${settingsPenaltyAmount}% of quarter amount`
                                  : settingsPenaltyAmount}
                            </small>
                          )}
                        </Col>
                      </FormGroup>

                      {globalError && <Alert color="danger">{globalError}</Alert>}

                      <Button
                        color="warning"
                        disabled={!isSettingsValid || updatingSettings}
                        onClick={() => setShowSettingsConfirm(true)}
                      >
                        {updatingSettings ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Updating...
                          </>
                        ) : (
                          'Update Settings'
                        )}
                      </Button>
                    </>
                  )}

                  {activeAction === 'create' && (
                    <>
                      <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                        Creates 4 quarterly transactions for every child enrolled in each selected
                        batch. Children who already have transactions will be skipped.
                      </p>

                      {selectedBatches.length > 0 && (
                        <FormGroup>
                          <Label>Address per Batch</Label>
                          <Table bordered size="sm" className="mb-3">
                            <thead>
                              <tr>
                                <th>Batch</th>
                                <th>Address</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedBatches.map((batch) => (
                                <tr key={batch.value}>
                                  <td style={{ verticalAlign: 'middle' }}>{batch.label}</td>
                                  <td>
                                    <Input
                                      type="select"
                                      bsSize="sm"
                                      value={batchAddresses[batch.value] || ''}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setBatchAddresses((prev) => ({
                                          ...prev,
                                          [batch.value]: val
                                        }));
                                      }}
                                    >
                                      <option value="">Select address...</option>
                                      {ADDRESS_OPTIONS.map((addr) => (
                                        <option key={addr} value={addr}>
                                          {addr}
                                        </option>
                                      ))}
                                    </Input>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </FormGroup>
                      )}

                      {globalError && <Alert color="danger">{globalError}</Alert>}

                      <Button
                        color="success"
                        disabled={!isCreateValid || creating}
                        onClick={() => setShowCreateConfirm(true)}
                      >
                        {creating ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Creating...
                          </>
                        ) : (
                          'Create Transactions'
                        )}
                      </Button>
                    </>
                  )}
                </CardBody>
              </Card>

              {activeAction === 'discount' && results.length > 0 && (
                <Card>
                  <CardBody>
                    <h6 className="mb-3">Results</h6>
                    <Table bordered size="sm">
                      <thead>
                        <tr>
                          <th>Batch</th>
                          <th>Status</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((r) => (
                          <tr key={r.batchId}>
                            <td>{r.name}</td>
                            <td>
                              <Badge color={r.success ? 'success' : 'danger'}>
                                {r.success ? 'Applied' : 'Failed'}
                              </Badge>
                            </td>
                            <td style={{ fontSize: '0.85rem' }}>{r.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              )}

              {activeAction === 'settings' && settingsResults.length > 0 && (
                <Card>
                  <CardBody>
                    <h6 className="mb-3">Results</h6>
                    <Table bordered size="sm">
                      <thead>
                        <tr>
                          <th>Batch</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {settingsResults.map((r) => (
                          <tr key={r.batchId}>
                            <td>{r.batchName || r.batchId}</td>
                            <td>
                              {r.error ? (
                                <>
                                  <Badge color="danger">Failed</Badge>
                                  <span className="ml-2 text-danger" style={{ fontSize: '0.82rem' }}>
                                    {r.error}
                                  </span>
                                </>
                              ) : (
                                <Badge color="success">Updated</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              )}

              {activeAction === 'create' && createResults.length > 0 && (
                <Card>
                  <CardBody>
                    <h6 className="mb-3">Results</h6>
                    <Table bordered size="sm">
                      <thead>
                        <tr>
                          <th>Batch</th>
                          <th>Created</th>
                          <th>Skipped</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {createResults.map((r) => (
                          <tr key={r.batchId}>
                            <td>{r.batchName}</td>
                            <td>{r.error ? '—' : r.created}</td>
                            <td>{r.error ? '—' : r.skipped}</td>
                            <td>
                              {r.error ? (
                                <Badge color="danger">Failed</Badge>
                              ) : (
                                <Badge color="success">Done</Badge>
                              )}
                              {r.error && (
                                <span className="ml-2 text-danger" style={{ fontSize: '0.82rem' }}>
                                  {r.error}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              )}
            </Col>

            <Col lg={7}>
              {selectedBatches.length > 0 && activeAction === 'settings' && (
                <Card>
                  <CardBody>
                    <h6 className="mb-3">Current Installment Settings</h6>
                    <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                      <Table bordered size="sm" className="mb-0">
                        <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                          <tr>
                            <th>Batch</th>
                            <th>Due Day</th>
                            <th>Penalty Mode</th>
                            <th>Q1</th>
                            <th>Q2</th>
                            <th>Q3</th>
                            <th>Q4</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedBatches.map((batch) => {
                            const quarters = [1, 2, 3, 4].map((n) => {
                              const q = batch[`quarter${n}`] || (batch.quarters && batch.quarters[n - 1]);
                              return q;
                            });
                            return (
                              <tr key={batch.value}>
                                <td style={{ fontWeight: 500 }}>{batch.label}</td>
                                <td>{batch.dueDay ?? 10}</td>
                                <td>
                                  <Badge color={batch.penaltyMode === 'daily_flat' ? 'info' : 'secondary'} style={{ fontSize: '0.75rem' }}>
                                    {batch.penaltyMode === 'daily_flat' ? 'Per Day' : 'Percentage'}
                                  </Badge>
                                </td>
                                {quarters.map((q, i) => (
                                  <td key={i} style={{ fontSize: '0.8rem', color: '#555' }}>
                                    {q?.month
                                      ? new Date(q.month).toLocaleString('en-IN', { month: 'short', year: 'numeric' })
                                      : '—'}
                                    {q && (
                                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                        <div>A: {q.alert_day ?? '—'}</div>
                                        <div>P: {q.penalty_day ?? '—'}</div>
                                      </div>
                                    )}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                    <small className="text-muted d-block mt-2">A = Alert Day &nbsp;·&nbsp; P = Penalty Day</small>
                  </CardBody>
                </Card>
              )}

              {selectedBatches.length > 0 && activeAction !== 'settings' && (
                <Card>
                  <CardBody>
                    <h6 className="mb-3">
                      Children Preview
                      {previewLoading && <Spinner size="sm" className="ml-2" />}
                    </h6>
                    {!previewLoading && previewRows.length === 0 && (
                      <p className="text-muted">No children found in selected batches.</p>
                    )}
                    {!previewLoading && previewRows.length > 0 && (
                      <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                        <Table bordered size="sm" className="mb-0">
                          <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                            <tr>
                              <th>Batch</th>
                              <th>Child</th>
                              <th>Quarter</th>
                              <th style={{ textAlign: 'right' }}>Amount</th>
                              <th style={{ textAlign: 'right' }}>Discount</th>
                              <th style={{ textAlign: 'right' }}>Net Payable</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewRows.map((row) => {
                              const discountAmt = row.existingDiscount > 0 ? row.existingDiscount : null;
                              const netPayable =
                                discountAmt != null && row.amountPerTx != null
                                  ? Number((row.amountPerTx - discountAmt).toFixed(2))
                                  : null;
                              const fmt = (v) => `₹${v.toLocaleString('en-IN')}`;
                              const dash = <span className="text-muted">—</span>;
                              return (
                                <tr key={`${row.batchId}-${row.childId}-Q${row.quarter}`}>
                                  {row.showBatch && (
                                    <td
                                      rowSpan={row.batchSpan}
                                      style={{ fontSize: '0.82rem', color: '#555', verticalAlign: 'top' }}
                                    >
                                      {row.batchName}
                                      {row.batchTotalFee != null && (
                                        <div
                                          className="text-muted"
                                          style={{ fontSize: '0.78rem', marginTop: 3 }}
                                        >
                                          Total Fees: ₹{row.batchTotalFee.toLocaleString('en-IN')}
                                        </div>
                                      )}
                                      {row.batchPromoPercent != null && (
                                        <div
                                          style={{ fontSize: '0.78rem', marginTop: 3, color: '#c0392b' }}
                                        >
                                          Promo: {row.batchPromoPercent}% = ₹
                                          {row.batchTotalDiscount?.toLocaleString('en-IN')}
                                        </div>
                                      )}
                                    </td>
                                  )}
                                  {row.showChild && (
                                    <td rowSpan={row.childSpan} style={{ verticalAlign: 'top' }}>
                                      {row.childName}
                                    </td>
                                  )}
                                  <td style={{ color: '#888', fontSize: '0.82rem' }}>Q{row.quarter}</td>
                                  <td style={{ textAlign: 'right' }}>
                                    {row.amountPerTx != null ? fmt(row.amountPerTx) : dash}
                                  </td>
                                  <td style={{ textAlign: 'right', color: '#c0392b' }}>
                                    {discountAmt != null ? `- ${fmt(discountAmt)}` : dash}
                                  </td>
                                  <td style={{ textAlign: 'right', fontWeight: 500 }}>
                                    {netPayable != null ? fmt(netPayable) : dash}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>

          {showConfirm && (
            <SweetAlert
              warning
              showCancel
              confirmBtnText="Yes, apply"
              cancelBtnText="Cancel"
              confirmBtnBsStyle="primary"
              cancelBtnBsStyle="secondary"
              title="Apply Promotional Discount?"
              onConfirm={handleApply}
              onCancel={() => setShowConfirm(false)}
            >
              You are about to apply a flat <strong>₹{discountAmountNum} discount</strong> to{' '}
              <strong>
                {selectedBatches.length} batch{selectedBatches.length > 1 ? 'es' : ''}
              </strong>
              . This cannot be undone. Only children currently enrolled will receive the discount.
            </SweetAlert>
          )}

          {showCreateConfirm && (
            <SweetAlert
              warning
              showCancel
              confirmBtnText="Yes, create"
              cancelBtnText="Cancel"
              confirmBtnBsStyle="success"
              cancelBtnBsStyle="secondary"
              title="Create Transactions?"
              onConfirm={handleCreate}
              onCancel={() => setShowCreateConfirm(false)}
            >
              This will create 4 quarterly transactions for every child in{' '}
              <strong>
                {selectedBatches.length} batch{selectedBatches.length > 1 ? 'es' : ''}
              </strong>
              . Children who already have transactions will be skipped.
            </SweetAlert>
          )}

          {showSettingsConfirm && (
            <SweetAlert
              warning
              showCancel
              confirmBtnText="Yes, update"
              cancelBtnText="Cancel"
              confirmBtnBsStyle="warning"
              cancelBtnBsStyle="secondary"
              title="Update Installment Settings?"
              onConfirm={handleUpdateSettings}
              onCancel={() => setShowSettingsConfirm(false)}
            >
              Updating <strong>{selectedBatches.length} batch{selectedBatches.length > 1 ? 'es' : ''}</strong>,
              quarters <strong>{settingsQuarters.map(n => `Q${n}`).join(', ')}</strong>:
              <ul className="mb-0 mt-1" style={{ paddingLeft: 20, textAlign: 'left' }}>
                {settingsDueDayNum !== null && <li>Due Day → <strong>{settingsDueDayNum}</strong> (all quarters)</li>}
                {settingsAlertDayNum !== null && <li>Alert Day → <strong>{settingsAlertDayNum}</strong></li>}
                {settingsPenaltyDayNum !== null && <li>Penalty Day → <strong>{settingsPenaltyDayNum}</strong></li>}
                {settingsPenaltyMode !== '' && <li>Penalty Mode → <strong>{settingsPenaltyMode === 'daily_flat' ? 'Per Day (Flat)' : 'Percentage'}</strong></li>}
                {settingsPenaltyAmount !== '' && <li>Penalty Amount → <strong>{settingsPenaltyMode === 'daily_flat' ? `₹${settingsPenaltyAmount}/day` : `${settingsPenaltyAmount}%`}</strong></li>}
              </ul>
            </SweetAlert>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({ authUser: state.Login.authUser });
export default connect(mapStateToProps)(PromotionalDiscount);
